import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Chip, CircularProgress, IconButton, Paper, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { setupPdfWorker } from "utils/setupPdfWorker";
import { buildPdfFileSource } from "../utils/pdfFileSource";
import { pdfToScreen, screenToPdf } from "../utils/pdfCoordinates";

setupPdfWorker();

const RENDER_WIDTH = 720;

const keyLabel = (keyMeta, key) => {
	if (!keyMeta) return key;
	if (typeof keyMeta === "string") return keyMeta;
	return keyMeta.label || keyMeta.key || key;
};

const PlaceholderChip = ({ field, label, scale, pageHeightPt, coordinateOrigin, selected, onSelect, onMove }) => {
	const pos = pdfToScreen(field.x, field.y, pageHeightPt, scale, coordinateOrigin);
	const dragRef = useRef({ dragging: false, startX: 0, startY: 0, originX: 0, originY: 0 });
	const onMoveRef = useRef(onMove);
	onMoveRef.current = onMove;

	const handlePointerDown = (e) => {
		e.preventDefault();
		e.stopPropagation();
		onSelect(field.key);
		dragRef.current = {
			dragging: true,
			startX: e.clientX,
			startY: e.clientY,
			originX: pos.x,
			originY: pos.y,
		};
	};

	useEffect(() => {
		const handleMove = (e) => {
			if (!dragRef.current.dragging) return;
			const dx = e.clientX - dragRef.current.startX;
			const dy = e.clientY - dragRef.current.startY;
			const screenX = Math.max(0, dragRef.current.originX + dx);
			const screenY = Math.max(0, dragRef.current.originY + dy);
			const pdf = screenToPdf(screenX, screenY, pageHeightPt, scale, coordinateOrigin);
			onMoveRef.current(field.key, { x: pdf.x, y: pdf.y });
		};
		const handleUp = () => {
			dragRef.current.dragging = false;
		};
		window.addEventListener("pointermove", handleMove);
		window.addEventListener("pointerup", handleUp);
		return () => {
			window.removeEventListener("pointermove", handleMove);
			window.removeEventListener("pointerup", handleUp);
		};
	}, [coordinateOrigin, field.key, pageHeightPt, scale]);

	return (
		<Box
			onPointerDown={handlePointerDown}
			sx={{
				position: "absolute",
				left: pos.x,
				top: pos.y,
				transform: "translate(-4px, -100%)",
				zIndex: selected ? 3 : 2,
				cursor: "grab",
				userSelect: "none",
			}}
		>
			<Chip
				size="small"
				label={label}
				color={selected ? "primary" : "default"}
				sx={{
					fontWeight: 600,
					boxShadow: 2,
					"& .MuiChip-label": { fontSize: "0.7rem" },
				}}
			/>
		</Box>
	);
};

const PdfPlaceholderEditor = ({
	sourcePdfUrl,
	fields,
	coordinateOrigin,
	availableKeys = [],
	unplacedKeys = [],
	onFieldsChange,
	onSelectKey,
	selectedKey,
}) => {
	const [numPages, setNumPages] = useState(0);
	const [pageNumber, setPageNumber] = useState(1);
	const [pageHeightPt, setPageHeightPt] = useState(792);
	const [pageWidthPt, setPageWidthPt] = useState(612);
	const [pdfError, setPdfError] = useState(null);
	const token = useSelector((state) => state.token);

	const pdfFile = useMemo(
		() => buildPdfFileSource(sourcePdfUrl, token),
		[sourcePdfUrl, token]
	);

	const scale = pageWidthPt > 0 ? RENDER_WIDTH / pageWidthPt : 1;
	const renderHeight = pageHeightPt * scale;

	const keyMetaByKey = useMemo(() => {
		const map = {};
		(availableKeys || []).forEach((k) => {
			const key = typeof k === "string" ? k : k.key;
			if (key) map[key] = k;
		});
		return map;
	}, [availableKeys]);

	const fieldsRef = useRef(fields);
	fieldsRef.current = fields;

	const pageFields = useMemo(
		() => (fields || []).filter((f) => (f.page ?? 0) === pageNumber - 1),
		[fields, pageNumber]
	);

	const handlePageLoad = useCallback((page) => {
		const vp = page.getViewport({ scale: 1 });
		setPageWidthPt(vp.width);
		setPageHeightPt(vp.height);
	}, []);

	const handleMove = useCallback(
		(key, { x, y }) => {
			onFieldsChange(
				(fieldsRef.current || []).map((f) =>
					f.key === key ? { ...f, x, y, page: pageNumber - 1 } : f
				)
			);
		},
		[onFieldsChange, pageNumber]
	);

	const handleAddUnplaced = (key) => {
		if ((fields || []).some((f) => f.key === key)) return;
		const next = [
			...(fields || []),
			{
				key,
				page: pageNumber - 1,
				x: 72,
				y: coordinateOrigin === "top_left" ? 72 : 700,
				font_size: 11,
			},
		];
		onFieldsChange(next);
		onSelectKey?.(key);
	};

	if (!sourcePdfUrl) {
		return (
			<AlertPlaceholder message="No PDF URL available for this template." />
		);
	}

	return (
		<Box display="flex" gap={2} flexWrap="wrap">
			<Box flex="1 1 480px" minWidth={320}>
				<Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
					<Typography variant="subtitle2">
						Page {pageNumber} of {numPages || "…"}
					</Typography>
					<Box>
						<IconButton
							size="small"
							disabled={pageNumber <= 1}
							onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
						>
							<ChevronLeftIcon />
						</IconButton>
						<IconButton
							size="small"
							disabled={!numPages || pageNumber >= numPages}
							onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
						>
							<ChevronRightIcon />
						</IconButton>
					</Box>
				</Box>

				<Paper
					variant="outlined"
					sx={{
						position: "relative",
						overflow: "hidden",
						width: RENDER_WIDTH,
						maxWidth: "100%",
						minHeight: renderHeight || 400,
						bgcolor: "grey.200",
					}}
				>
					{pdfError && (
						<Box p={2}>
							<Typography color="error">{pdfError}</Typography>
						</Box>
					)}
					<Document
						file={pdfFile}
						onLoadSuccess={({ numPages: n }) => {
							setNumPages(n);
							setPdfError(null);
						}}
						onLoadError={(err) => setPdfError(err?.message || "Failed to load PDF.")}
						loading={
							<Box display="flex" justifyContent="center" p={4}>
								<CircularProgress />
							</Box>
						}
					>
						<Box sx={{ position: "relative", width: RENDER_WIDTH, height: renderHeight }}>
							<Page
								pageNumber={pageNumber}
								width={RENDER_WIDTH}
								onLoadSuccess={handlePageLoad}
								renderTextLayer={false}
								renderAnnotationLayer={false}
							/>
							{pageFields.map((field) => (
								<PlaceholderChip
									key={field.key}
									field={field}
									label={keyLabel(keyMetaByKey[field.key], field.key)}
									scale={scale}
									pageHeightPt={pageHeightPt}
									coordinateOrigin={coordinateOrigin}
									selected={selectedKey === field.key}
									onSelect={onSelectKey}
									onMove={handleMove}
								/>
							))}
						</Box>
					</Document>
				</Paper>
				<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
					Drag labels onto the PDF. Coordinates save as PDF points ({coordinateOrigin}).
				</Typography>
			</Box>

			<Box flex="0 1 260px" minWidth={220}>
				<Typography variant="subtitle2" gutterBottom>
					Unplaced variables
				</Typography>
				<Box display="flex" flexWrap="wrap" gap={1} mb={2}>
					{(unplacedKeys || []).length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							All keys placed on this template.
						</Typography>
					) : (
						unplacedKeys.map((key) => (
							<Chip
								key={key}
								label={keyLabel(keyMetaByKey[key], key)}
								size="small"
								onClick={() => handleAddUnplaced(key)}
								sx={{ cursor: "pointer" }}
								variant="outlined"
							/>
						))
					)}
				</Box>

				<Typography variant="subtitle2" gutterBottom>
					On this page
				</Typography>
				<Box display="flex" flexDirection="column" gap={0.5}>
					{pageFields.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							No placeholders on this page.
						</Typography>
					) : (
						pageFields.map((f) => (
							<Chip
								key={f.key}
								label={`${f.key} (${Math.round(f.x)}, ${Math.round(f.y)})`}
								size="small"
								color={selectedKey === f.key ? "primary" : "default"}
								onClick={() => onSelectKey?.(f.key)}
								onDelete={() =>
									onFieldsChange((fields || []).filter((x) => x.key !== f.key))
								}
								sx={{ justifyContent: "space-between" }}
							/>
						))
					)}
				</Box>
			</Box>
		</Box>
	);
};

const AlertPlaceholder = ({ message }) => (
	<Paper variant="outlined" sx={{ p: 3 }}>
		<Typography color="text.secondary">{message}</Typography>
	</Paper>
);

export default PdfPlaceholderEditor;
